import {
  Box,
  Button,
  CardMedia,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Batch, Job, JobStatus, SheetData } from "../../pages/playground/types";
import { useEffect, useState } from "react";
import { ArrowBackIos, ArrowForwardIos, Close } from "@mui/icons-material";
import { updateJobClientFeedback, updateJobStatus } from "../../api/batches";
import FormattedText from "../common/formatted-text";
import PhraseGroup from "../common/phrase-group";
import { getEnabledGroups } from "../../utils/content-groups";

export default function ClientJobDialog({
  updateJob,
  job,
  batch,
  sheetData,
  onClose,
  onRefresh,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
}: {
  updateJob: (job: Job) => void;
  job: Job;
  batch: Batch;
  sheetData: SheetData;
  onClose?: () => void;
  onRefresh?: (toDeleteJobIds?: string[]) => void;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isAcceptingCompleted, setIsAcceptingCompleted] = useState(false);
  const [isAcceptingToBeReworked, setIsAcceptingToBeReworked] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveTimestamp, setSaveTimestamp] = useState<Date | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (job.id) {
      setFeedback(job.user_feedback || "");
    }
    return () => {
      if (saveTimeout) clearTimeout(saveTimeout);
    };
  }, [job.id]);

  // Get master file data for this specific job
  const getMasterFileData = () => {
    if (
      !sheetData ||
      !sheetData.items ||
      !sheetData.columns ||
      !job.master_id
    ) {
      return null;
    }

    // Find the specific row that matches this job's SKU (master_id)
    const idColumnIndex = sheetData.columns[0]; // Assuming the first column is the ID/SKU column
    const jobData = sheetData.items.find(
      (item) =>
        String(item[idColumnIndex]).trim() === String(job.master_id).trim()
    );

    if (!jobData) {
      return null;
    }

    // Create an array of field-value pairs for rendering
    return sheetData.columns.map((column) => ({
      field: column,
      value: jobData[column],
    }));
  };

  const masterFileData = getMasterFileData();
  const enabledGroups = getEnabledGroups(batch?.settings);

  const renderContent = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          overflow: "hidden",
          height: "100%",
          "& ::-webkit-scrollbar": {
            width: "1px",
            height: "1px",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.3)",
            borderRadius: "1px",
            cursor: "pointer",
          },
          "& ::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0, 0, 0, 0.3)",
            cursor: "pointer",
          },
        }}
      >
        {/* Master file data table */}
        {masterFileData && masterFileData.length > 0 && (
          <Box
            sx={{
              overflow: "auto",
              maxHeight: "70vh",
              flex: 2,
              pr: 1,
            }}
          >
            <Box>
              {masterFileData.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 1,
                    borderBottom:
                      index < masterFileData.length - 1
                        ? "1px solid #f5f5f5"
                        : "none",
                    pb: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {row.field}
                  </Typography>
                  <Typography>
                    {row.value !== null && row.value !== undefined
                      ? String(row.value)
                      : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flex: 3,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              height: "60vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
            }}
          >
            {job.images && job.images.length > 0 ? (
              <>
                {job.images && job.images.length > 1 && (
                  <Box
                    onClick={() => {
                      const newIndex =
                        (selectedImageIndex - 1 + (job.images?.length || 0)) %
                        (job.images?.length || 0);
                      setSelectedImageIndex(newIndex);
                    }}
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      p: 1,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    <ArrowBackIos sx={{ fontSize: 24, color: "#B00119" }} />
                  </Box>
                )}

                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 400,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={job.images[selectedImageIndex]}
                      alt={job.title || "Product image"}
                      sx={{
                        maxHeight: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                        transform: "scale(1)",
                        transition: "transform 0.1s ease-out",
                        cursor: "crosshair",
                      }}
                      onMouseMove={(event) => {
                        const img = event.currentTarget;
                        const { left, top, width, height } =
                          img.getBoundingClientRect();
                        const x = (event.clientX - left) / width;
                        const y = (event.clientY - top) / height;

                        // Set transform origin to the mouse position
                        img.style.transformOrigin = `${x * 100}% ${y * 100}%`;
                        img.style.transform = "scale(2.5)";
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                  </Box>
                </Box>

                {job.images?.length > 1 && (
                  <Box
                    onClick={() => {
                      const newIndex =
                        (selectedImageIndex + 1) % (job.images?.length || 0);
                      setSelectedImageIndex(newIndex);
                    }}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      p: 1,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    <ArrowForwardIos sx={{ fontSize: 24, color: "#B00119" }} />
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No available images
              </Typography>
            )}
          </Box>

          {job.images && job.images.length > 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
              }}
            >
              {job.images.map((img: string, index: number) => (
                <Box
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  sx={{
                    width: 50,
                    height: 50,
                    cursor: "pointer",
                    border:
                      selectedImageIndex === index
                        ? "2px solid #B00119"
                        : "2px solid transparent",
                    borderRadius: 1,
                    overflow: "hidden",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={img}
                    alt={`Thumbnail ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Right side: Product information */}
        <Box sx={{ flex: 3, minWidth: 0 }}>
          <Box
            sx={{
              height: "70vh",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="body2">SKU: {job.master_id}</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    color:
                      job.status === JobStatus.DELIVERED ||
                      job.status === JobStatus.TO_BE_REWORKED ||
                      job.status === JobStatus.TO_BE_REVIEWED
                        ? "#B00119"
                        : "#43A047",
                  }}
                >
                  {job.status === JobStatus.DELIVERED && "To be approved"}
                  {job.status === JobStatus.COMPLETED && "Approved"}
                  {job.status === JobStatus.TO_BE_REWORKED && "To be reworked"}
                  {job.status === JobStatus.TO_BE_REVIEWED && "To be reviewed"}
                </Typography>
              </Box>

              {(() => {
                const titleGroup = enabledGroups.find((g) => g.kind === "title");
                const titlePhrases = titleGroup
                  ? (job.phrases || [])
                      .filter((p) => p.type === titleGroup.id)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                  : [];

                return titlePhrases.length > 0 ? (
                  <Typography variant="h5" component="h1" fontWeight="medium" mt={1}>
                    <PhraseGroup
                      phrases={titlePhrases.map((p) => p.phrase)}
                      formatting={titleGroup!.formatting}
                      mode="rendered"
                    />
                  </Typography>
                ) : (
                  <Typography variant="h5" component="h1" fontWeight="medium" mt={1}>
                    {job.title ? <FormattedText text={job.title} /> : "SKU without title"}
                  </Typography>
                );
              })()}
            </Box>

            {(() => {
              const exportRules = batch?.settings?.export_rules || [];
              const mergedGroupIds = new Set(
                exportRules.flatMap((r) => r.sourceGroups),
              );

              // Build unified list of sections (individual groups + rule
              // sections), then sort by sortOrder to mirror the CSV export.
              type Section = {
                key: string;
                label: string;
                sortOrder: number;
                items: {
                  id: string;
                  formatting: typeof enabledGroups[number]["formatting"];
                  phrases: string[];
                }[];
              };

              const sections: Section[] = [];

              // Individual group sections (skip title and merged groups)
              for (const group of enabledGroups) {
                if (group.kind === "title") continue;
                if (mergedGroupIds.has(group.id)) continue;
                const phrases = (job.phrases || [])
                  .filter((p) => p.type === group.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((p) => p.phrase);
                if (phrases.length === 0) continue;
                sections.push({
                  key: group.id,
                  label: group.label,
                  sortOrder: group.sortOrder,
                  items: [
                    {
                      id: group.id,
                      formatting: group.formatting,
                      phrases,
                    },
                  ],
                });
              }

              // Export rule sections: each rule renders as one section with
              // sub-blocks per source group (each keeping its own formatting
              // — mirrors the Python backend's "\n\n" concatenation).
              for (const rule of exportRules) {
                if (!rule.label || rule.sourceGroups.length === 0) continue;
                const items = rule.sourceGroups
                  .map((gid) => {
                    const g = enabledGroups.find((eg) => eg.id === gid);
                    if (!g) return null;
                    const phrases = (job.phrases || [])
                      .filter((p) => p.type === g.id)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((p) => p.phrase);
                    if (phrases.length === 0) return null;
                    return {
                      id: g.id,
                      formatting: g.formatting,
                      phrases,
                    };
                  })
                  .filter((x): x is NonNullable<typeof x> => x !== null);

                if (items.length === 0) continue;

                const minOrder = Math.min(
                  ...rule.sourceGroups.map(
                    (gid) =>
                      enabledGroups.find((g) => g.id === gid)?.sortOrder ?? 999,
                  ),
                );

                sections.push({
                  key: `rule:${rule.id}`,
                  label: rule.label,
                  sortOrder: minOrder,
                  items,
                });
              }

              sections.sort((a, b) => a.sortOrder - b.sortOrder);

              return sections.map((section) => (
                <Box key={section.key} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {section.label}
                  </Typography>
                  {section.items.map((item, i) => (
                    <Box key={item.id} sx={{ mt: i > 0 ? 1 : 0 }}>
                      <PhraseGroup
                        phrases={item.phrases}
                        formatting={item.formatting}
                        mode="rendered"
                      />
                    </Box>
                  ))}
                </Box>
              ));
            })()}

            <Box
              sx={{
                pt: 3,
                mt: "auto",
              }}
            >
              {job.client_notes && job.client_notes.length > 0 && (
                <Box sx={{ mb: 2, mt: "auto" }}>
                  <Typography variant="body2">
                    Notes from copywriters
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      paddingLeft: 2,
                      borderLeft: "1px solid #ccc",
                      fontSize: "0.85rem",
                      color: "text.secondary",
                    }}
                  >
                    {job.client_notes}
                  </Typography>
                </Box>
              )}

              {job.status === JobStatus.COMPLETED &&
                feedback &&
                feedback !== "" && (
                  <Box sx={{ mb: 2, mt: "auto" }}>
                    <Typography variant="body2">Client feedback</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        paddingLeft: 2,
                        borderLeft: "1px solid #ccc",
                        fontSize: "0.85rem",
                        color: "text.secondary",
                      }}
                    >
                      {feedback}
                    </Typography>
                  </Box>
                )}

              {job.status !== JobStatus.COMPLETED && (
                <Box sx={{ mb: 2, mt: "auto" }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Feedback
                    </Typography>
                    {job.status !== JobStatus.TO_BE_REWORKED && (
                      <>
                        {isSaving && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", mr: 1 }}
                          >
                            Saving...
                          </Typography>
                        )}
                        {!isSaving && saveTimestamp && (
                          <Typography
                            variant="caption"
                            sx={{ color: "success.main", mr: 1 }}
                          >
                            Saved
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  <TextField
                    sx={{ mt: 0 }}
                    fullWidth
                    multiline
                    size="small"
                    rows={4}
                    value={feedback}
                    placeholder={
                      job.status === JobStatus.TO_BE_REWORKED
                        ? "This job is read-only because it has been marked to be reworked."
                        : "Mandatory feedback to reject the job in TO BE REWORKED status."
                    }
                    disabled={job.status === JobStatus.TO_BE_REWORKED}
                    onChange={(e) => {
                      if (job.status === JobStatus.TO_BE_REWORKED) return;

                      setFeedback(e.target.value);

                      // Clear previous timeout
                      if (saveTimeout) {
                        clearTimeout(saveTimeout);
                      }

                      // Set saving indicator
                      setIsSaving(true);

                      // Set new timeout for saving
                      const timeout = setTimeout(async () => {
                        if (!job) return;

                        await updateJobClientFeedback({
                          id: job.id,
                          feedback: e.target.value,
                        }).then(() => {
                          updateJob({
                            ...job,
                            user_feedback: e.target.value,
                          });
                        });

                        setIsSaving(false);
                        setSaveTimestamp(new Date());
                      }, 500); // 0.5 second debounce

                      setSaveTimeout(timeout);
                    }}
                    variant="outlined"
                  />
                  {job.status === JobStatus.TO_BE_REWORKED && (
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        color: "text.secondary",
                        fontStyle: "italic",
                        display: "block",
                      }}
                    >
                      This job is read-only because it has been marked to be
                      reworked.
                    </Typography>
                  )}
                </Box>
              )}

              {job.status === JobStatus.DELIVERED && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    {(isAcceptingCompleted || isAcceptingToBeReworked) && (
                      <Box sx={{ flex: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                          }}
                        >
                          Are you sure?
                        </Typography>
                      </Box>
                    )}
                    {!isAcceptingCompleted && !isAcceptingToBeReworked && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={feedback.trim() === ""}
                          onClick={() => {
                            setIsAcceptingToBeReworked(true);
                          }}
                        >
                          Mark as To be reworked
                        </Button>
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          onClick={async () => {
                            setIsAcceptingCompleted(true);
                          }}
                        >
                          Mark as Approved
                        </Button>
                      </Box>
                    )}
                    {(isAcceptingCompleted || isAcceptingToBeReworked) && (
                      <Box sx={{ flex: 3 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Button
                              variant="outlined"
                              color={isAcceptingCompleted ? "error" : "success"}
                              size="small"
                              onClick={async () => {
                                setIsAcceptingCompleted(false);
                                setIsAcceptingToBeReworked(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                          <Box sx={{ flex: 3 }}>
                            <Button
                              variant="outlined"
                              color={isAcceptingCompleted ? "success" : "error"}
                              size="small"
                              onClick={async () => {
                                const nextStatus = isAcceptingCompleted
                                  ? JobStatus.COMPLETED
                                  : JobStatus.TO_BE_REWORKED;

                                await updateJobStatus({
                                  id: job.id,
                                  status: nextStatus,
                                  feedback: feedback,
                                });

                                setIsAcceptingCompleted(false);
                                setIsAcceptingToBeReworked(false);

                                if (onNext) {
                                  onNext();
                                } else if (onClose) {
                                  onClose();
                                }

                                if (onRefresh) {
                                  onRefresh();
                                }
                              }}
                            >
                              {isAcceptingCompleted
                                ? "Mark as Approved"
                                : "Mark as To be reworked"}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth="xl"
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: { onClick: onClose },
      }}
    >
      {/* Navigation & close buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
        }}
      >
        <Box>
          <IconButton
            aria-label="previous"
            onClick={() => onPrevious?.()}
            disabled={!hasPrevious}
            size="small"
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="next"
            onClick={() => onNext?.()}
            disabled={!hasNext}
            size="small"
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, overflow: "hidden", display: "flex", flexGrow: 1 }}>
        {renderContent()}
      </Box>
    </Dialog>
  );
}
